// Libraries
import { flexRender } from '@tanstack/react-table';
// Components
import { Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react';
// Types
import { SummaryTableLayoutProps } from './SummaryTable.types';

/**
 * Component representing the layout of a summary table.
 *
 * @component
 * @param {SummaryTableLayoutProps} props - The component props.
 * @param {Table<SummaryTableBody>} props.table - Table instance for build the table
 * @returns {ReactElement} The SummaryTableLayout component.
 */
export const SummaryTableLayout = ({ table }: SummaryTableLayoutProps) => {
  return (
    <Table>
      {/* Render the table header */}
      <Thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <Th
                  key={header.id}
                  colSpan={header.colSpan}
                  w={header.getSize()}
                  position='relative'
                  minW={8}
                  minH={8}
                  textTransform='none'
                  p={0}
                  _first={{ borderRight: 'section' }}
                  borderBottom='section'
                  backgroundColor='table.header'
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      {/* Render the table body */}
      <Tbody>
        {table.getRowModel().rows.map((row) => {
          return (
            <Tr key={row.id} h={16}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <Td
                    borderBottom='section'
                    key={cell.id}
                    w={cell.column.getSize()}
                    _first={{ borderRight: 'section' }}
                    minW={8}
                    minH={8}
                    p={0}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
      {/* Render the table footer */}
      <Tfoot>
        {table.getFooterGroups().map((footerGroup) => (
          <Tr key={footerGroup.id} backgroundColor='table.footer'>
            {footerGroup.headers.map((cell) => (
              <Td
                key={cell.id}
                w={cell.column.getSize()}
                _first={{ borderRight: 'section' }}
                borderBottom='none'
                p={0}
                h={16}
              >
                {cell.isPlaceholder ? null : flexRender(cell.column.columnDef.footer, cell.getContext())}
              </Td>
            ))}
          </Tr>
        ))}
      </Tfoot>
    </Table>
  );
};
